import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import {
  Save,
  X,
  CheckCircle2,
  AlertCircle,
  ShieldAlert,
  KeyRound,
  Loader2,
  Info,
} from 'lucide-react';
import { ClientResponseError } from 'pocketbase';
import { currentUser, pb } from '~/lib/pb';
import { forceLogout } from '~/lib/session';
import {
  configRouteRedirect,
  readSelectedTenantId,
  superadminDeepConfigTarget,
} from '~/lib/config-access';
import {
  applyProfileLoad,
  initialProfileLoadState,
  profileLoadView,
  type ProfileData,
} from '~/lib/profile-load';
import { PasswordInput } from '~/components/PasswordInput/PasswordInput';
import { Tooltip } from '~/components/Tooltip/Tooltip';
// El área del cliente es la misma para todos los roles, también si un
// superadmin está impersonando. Chatbot, embudos y estructura del sitio
// viven en la ficha del cliente (/clientes/$id). Equipo vive en /team.
import styles from './_authed.settings.module.css';

// Ícono de ayuda reusable: ancla un Tooltip junto a un título/label en vez de
// dejar el texto descriptivo suelto debajo.
const helpIconStyle: React.CSSProperties = {
  display: 'inline-flex',
  verticalAlign: 'middle',
  marginLeft: 6,
  color: 'var(--color-text-subtle)',
  cursor: 'help',
};

function HelpTip({ content }: { content: React.ReactNode }) {
  return (
    <Tooltip content={content}>
      <span style={helpIconStyle}>
        <Info size={14} aria-hidden />
      </span>
    </Tooltip>
  );
}

interface SettingsSearch {
  tab?: Tab;
}

export const Route = createFileRoute('/_authed/settings')({
  beforeLoad: ({ search }) => {
    const tab = typeof search.tab === 'string' ? search.tab : undefined;
    const dest = configRouteRedirect(currentUser()?.role, '/settings', tab);
    if (dest) throw redirect(dest);
    if (currentUser()?.role === 'superadmin') {
      const deep = superadminDeepConfigTarget({ tab }, readSelectedTenantId());
      if (deep) throw redirect(deep);
    }
  },
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const t = search.tab;
    if (
      t === 'profile' ||
      t === 'chatbot' ||
      t === 'schemas' ||
      t === 'pipelines' ||
      t === 'notifications' ||
      t === 'team'
    ) {
      return { tab: t };
    }
    return {};
  },
  component: SettingsPage,
});

type Tab = 'profile' | 'chatbot' | 'schemas' | 'pipelines' | 'notifications' | 'team';

function SettingsPage() {
  const me = currentUser();

  if (!me) {
    return (
      <div className={styles.page}>
        <div className={styles.notAllowed}>
          <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
          <p>Sesión inválida — vuelve a entrar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className="h2">Mi perfil</h1>
          <p className={styles.subtitle}>
            Tu nombre, teléfono y clave. La configuración del cliente la hace Life Focus.
          </p>
        </div>
      </header>

      <div className={styles.layout}>
        <main className={styles.main}>
          <ProfileForm userId={me.id} showTitle={false} />
        </main>
      </div>
    </div>
  );
}

// ── Generic Field wrapper ──────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>
        {label}
        {hint && <HelpTip content={hint} />}
      </span>
      {children}
    </label>
  );
}

// ── Profile form ───────────────────────────────────────────────────

function ProfileForm({ userId, showTitle = true }: { userId: string; showTitle?: boolean }) {
  // Cargamos el record fresco desde DB para tener role/email/display_name
  // actualizados (no del JWT que puede estar viejo).
  const [load, setLoad] = useState(initialProfileLoadState);
  const [form, setForm] = useState<ProfileData>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const me = load.me;

  useEffect(() => {
    let cancelled = false;
    setLoad(initialProfileLoadState());
    pb.collection('users')
      .getOne<{
        id: string;
        email: string;
        display_name?: string;
        phone?: string;
      }>(userId)
      .then((rec) => {
        const next: ProfileData = {
          display_name: rec.display_name ?? '',
          phone: rec.phone ?? '',
          email: rec.email,
        };
        setLoad((state) => applyProfileLoad(state, { type: 'success', cancelled, profile: next }));
        if (!cancelled) setForm(next);
      })
      .catch(() => {
        setLoad((state) => applyProfileLoad(state, { type: 'failure', cancelled }));
      })
      .finally(() => {
        setLoad((state) => applyProfileLoad(state, { type: 'finally', cancelled }));
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      await pb.collection('users').update(userId, {
        display_name: form.display_name ?? '',
        phone: form.phone ?? '',
      });
      setLoad((state) =>
        applyProfileLoad(state, { type: 'success', cancelled: false, profile: form }),
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof ClientResponseError
          ? err.response?.message || err.message
          : 'No pudimos guardar.',
      );
    } finally {
      setSaving(false);
    }
  }

  const view = profileLoadView(load);
  if (view === 'loading') {
    return <div className={styles.empty}>Cargando…</div>;
  }
  if (view === 'error') {
    return (
      <div className={styles.formWrap}>
        {showTitle && (
          <header className={styles.formHeader}>
            <h2 className={styles.formTitle}>Mi perfil</h2>
          </header>
        )}
        <div className={styles.errorBanner} role="alert">
          <AlertCircle size={14} aria-hidden /> {load.loadError}
        </div>
      </div>
    );
  }

  const hasChanges = form.display_name !== me.display_name || form.phone !== me.phone;

  return (
    <div className={styles.formWrap}>
      {showTitle && (
        <header className={styles.formHeader}>
          <h2 className={styles.formTitle}>
            Mi perfil
            <HelpTip content="Estos datos identifican tu cuenta. El correo y el rol los administra Life Focus." />
          </h2>
        </header>
      )}

      <form onSubmit={handleSave} className={styles.form}>
        <Field
          label="Correo"
          hint="No se cambia desde aquí. Si necesitas otro, pídeselo a Life Focus."
        >
          <input type="email" className={styles.input} value={me.email ?? ''} disabled readOnly />
        </Field>

        <Field label="Nombre">
          <input
            type="text"
            className={styles.input}
            value={form.display_name ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            disabled={saving}
            placeholder="Tu nombre completo"
          />
        </Field>

        <Field label="Teléfono">
          <input
            type="tel"
            className={styles.input}
            value={form.phone ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            disabled={saving}
            placeholder="+56 9 1234 5678"
          />
        </Field>

        {saveError && (
          <div className={styles.errorBanner} role="alert">
            <AlertCircle size={14} aria-hidden /> {saveError}
          </div>
        )}
        {saved && (
          <div className={styles.successBanner}>
            <CheckCircle2 size={14} aria-hidden /> Guardado
          </div>
        )}

        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setShowPasswordModal(true)}
          >
            <KeyRound size={14} aria-hidden /> Cambiar contraseña
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <Loader2 size={14} aria-hidden className={styles.spinIcon} />
                Guardando…
              </>
            ) : (
              <>
                <Save size={14} aria-hidden /> Guardar cambios
              </>
            )}
          </button>
        </div>
      </form>

      {showPasswordModal && (
        <ChangePasswordModal userId={userId} onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}

// ── Change password modal ──────────────────────────────────────────

interface ChangePasswordModalProps {
  userId: string;
  onClose: () => void;
}

function ChangePasswordModal({ userId, onClose }: ChangePasswordModalProps) {
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      await pb.collection('users').update(userId, {
        oldPassword,
        password,
        passwordConfirm: confirm,
      });
      // PB invalida el JWT al cambiar la clave — salir YA con mensaje claro
      // (si no, las queries fallan en silencio y se ven pantallas vacías).
      forceLogout('password_changed');
    } catch (err) {
      const msg =
        err instanceof ClientResponseError
          ? err.status === 400
            ? 'Contraseña actual incorrecta.'
            : err.response?.message || err.message
          : 'No pudimos cambiar tu contraseña.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <header className={styles.modalHeader}>
          <h3 id="change-password-title" className={styles.modalTitle}>
            Cambiar contraseña
          </h3>
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
            <X size={16} aria-hidden />
          </button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          <Field label="Contraseña actual">
            <PasswordInput
              className={styles.input}
              autoComplete="current-password"
              required
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </Field>

          <Field label="Nueva contraseña" hint="Mínimo 8 caracteres.">
            <PasswordInput
              className={styles.input}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={saving}
            />
          </Field>

          <Field label="Confirmar nueva contraseña">
            <PasswordInput
              className={styles.input}
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={saving}
            />
          </Field>

          {error && (
            <div className={styles.errorBanner}>
              <AlertCircle size={14} aria-hidden /> {error}
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 size={14} aria-hidden className={styles.spinIcon} />
                  Cambiando…
                </>
              ) : (
                'Cambiar contraseña'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
